import React, { useEffect, useMemo, useState } from "react";
import * as api from "API";

const overlayClass =
  "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4";

const modalClass =
  "w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-gray-200";

const headerClass =
  "flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4";

const bodyClass = "px-6 py-5";

const footerClass =
  "flex flex-col-reverse gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-end";

const labelClass = "mb-1 block text-sm font-medium text-gray-700";
const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20";
// const disabledInputClass =
//   "w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600";
const sectionTitleClass = "text-sm font-semibold text-gray-900";
const helperClass = "mt-1 text-xs text-gray-500";

const defaultForm = {
  // vehicle
  make: "",
  model: "",
  year: "",
  odo: "",
  plate: "",
  // customer
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  // services
  serviceId: "",
  totalMinutes: "",
  time: ""
};

const CreateAppointmentModal = ({
  open = false,
  onClose = () => { },
  onCreate = async (payload) => {
    console.log("Create payload:", payload);
  },
  // readonly date (string like "2026-01-28")
  date = "",
  // services list: [{ id: "1", name: "Oil Change" }]
  services = [],
}) => {
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(defaultForm);
    setError("");
  }, [open]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const canSubmit = useMemo(() => {
    if (!date) return false;
    if (!form.make.trim()) return false;
    if (!form.model.trim()) return false;
    if (!form.year.trim()) return false;
    if (!form.odo.toString().trim()) return false;
    if (!form.customerName.trim()) return false;
    if (!form.customerPhone.trim()) return false;
    if (!form.customerEmail.trim()) return false;
    if (!form.serviceId) return false;
    return true;
  }, [form, date]);

  const setField = (key) => (e) => {
    setError("");
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleOverlayClick = (e) => {
    // close only if user clicks the backdrop (not the modal)
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setError("");

    try {
      const payload = {
        date, // readonly
        vehicle: {
          make: form.make.trim(),
          model: form.model.trim(),
          year: form.year.trim(),
          odo: Number(form.odo),
          plate: form.plate
        },
        name: form.customerName.trim(),
        phone: form.customerPhone.trim(),
        email: form.customerEmail.trim(),
        time: form.time, services: [form.serviceId],
        totalMinutes: Number(form.totalMinutes)
      };
      console.log("Payload created and submittings")
      await api.createAppointment(payload);
      onClose();
    } catch (err) {
      setError(err?.message || "Something went wrong while creating appointment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className={overlayClass} onMouseDown={handleOverlayClick} role="dialog" aria-modal="true">
      <div className={modalClass}>
        {/* Header */}
        <div className={headerClass}>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create Appointment</h2>
            <p className="mt-1 text-sm text-gray-500">Fill the details below and press Create.</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className={bodyClass}>
            {error ? (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            {/* vehicle */}
            <div className="mb-6">
              <div className="mb-3 flex items-center justify-between">
                <p className={sectionTitleClass}>vehicle</p>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className={labelClass}>Make</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={form.make}
                    onChange={setField("make")}
                    placeholder="e.g., Toyota"
                  />
                </div>

                <div>
                  <label className={labelClass}>Model</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={form.model}
                    onChange={setField("model")}
                    placeholder="e.g., Camry"
                  />
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <input
                    type="number"
                    className={inputClass}
                    value={form.year}
                    onChange={setField("year")}
                    placeholder="e.g., 2019"
                    min="1900"
                    max="2100"
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="mt-4">
                  <label className={labelClass}>Mileage</label>
                  <input
                    type="number"
                    className={inputClass}
                    value={form.odo}
                    onChange={setField("odo")}
                    placeholder="e.g., 125000"
                    min="0"
                  />
                </div>

                <div className="mt-4">
                  <label className={labelClass}>Plate Number</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={form.plate}
                    onChange={setField("plate")}
                    placeholder="e.g., AX23232"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Customer */}
            <div className="mb-6">
              <p className={sectionTitleClass}>Customer</p>

              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Name</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={form.customerName}
                    onChange={setField("customerName")}
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Contact Details</label>
                  <input
                    type="tel"
                    className={inputClass}
                    value={form.customerPhone}
                    onChange={setField("customerPhone")}
                    placeholder="Phone number"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className={labelClass}>Email</label>
                  <input
                    type="email"
                    className={inputClass}
                    value={form.customerEmail}
                    onChange={setField("customerEmail")}
                    placeholder="name@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Services + Date */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Primary Service</label>
                <select className={inputClass} value={form.serviceId} onChange={setField("serviceId")}>
                  <option value="">Select a service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <p className={helperClass}>Choose one service for this appointment.</p>
              </div>

              <div>
                <label className={labelClass}>Appointment Date & Time</label>
                <input
                  type="datetime-local"
                  className={inputClass}
                  value={form.time || ""}
                  onChange={setField("time")}
                />
                <p className={helperClass}>
                  Select the date and time for this appointment.
                </p>
              </div>
            </div>

            <div className="mt-8">
              <label className={labelClass}>Estimated Total Minutes</label>
              <input type="text" className={`${inputClass}`} value={form.totalMinutes || ""} onChange={setField("totalMinutes")} />
              <p className={helperClass}>Enter the estimated total minutes for this appointment.</p>
            </div>
          </div>

          {/* Footer */}
          <div className={footerClass}>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitting ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAppointmentModal;
